import { Link } from "@tanstack/react-router";
import { ShieldCheck, MapPin, Phone, Mail, BadgeCheck, Globe, Instagram, Link2, ArrowRight } from "lucide-react";
import { getBusinessConfig } from "@/lib/site-config";
import { trackEvent } from "@/lib/tracking";

/**
 * Trust card rendered on the homepage that mirrors the canonical record at
 * /official-business-information. It's the human-readable evidence pack that
 * proves we are a real, owned, DVSA-authorised business — matching the
 * sameAs schema and Google Business Profile appeal pack.
 */
export function VerifiedBusinessCard() {
  const biz = getBusinessConfig();
  return (
    <section className="section-y-tight">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="rounded-3xl border border-border bg-card overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr]">
            <div className="p-7 md:p-10 border-b lg:border-b-0 lg:border-r border-border">
              <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-primary">
                <ShieldCheck className="h-3.5 w-3.5" /> Verified business details
              </div>
              <h2 className="mt-4 text-2xl md:text-3xl font-extrabold text-foreground tracking-tight">
                A real garage. A real workshop. A real DVSA-authorised MOT station.
              </h2>
              <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
                Every detail below is the same on our Google Business Profile,
                Companies House record and DVSA MOT registration. One business,
                one phone number, one yard. No middlemen, no franchise.
              </p>

              <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Fact icon={BadgeCheck} label="DVSA MOT Station">
                  VTS {biz.vtsNumber} · Classes 1, 2, 3, 4, 5 & 7
                </Fact>
                <Fact icon={MapPin} label="Workshop">
                  14 Copenhagen Place, London E14 7DF
                </Fact>
                <Fact icon={MapPin} label="Registered office">
                  {biz.address}, {biz.addressLine2}, {biz.postcode}
                </Fact>
                <Fact icon={Phone} label="Direct line">
                  <a
                    href={biz.phoneHref}
                    onClick={() => trackEvent("call_click", { location: "verified_card" })}
                    className="text-primary font-bold hover:underline"
                  >
                    {biz.phone}
                  </a>
                </Fact>
                <Fact icon={Mail} label="Domain email">
                  <a href={`mailto:${biz.email}`} className="text-primary font-bold hover:underline break-all">
                    {biz.email}
                  </a>
                </Fact>
                <Fact icon={Globe} label="Official website">
                  woodseerautos.co.uk
                </Fact>
              </dl>

              <div className="mt-6 flex flex-wrap items-center gap-2">
                <Link
                  to="/official-business-information"
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-bold text-primary-foreground hover:bg-primary/90"
                >
                  View official business record <ArrowRight className="h-3.5 w-3.5" />
                </Link>
                <a
                  href={biz.googleReviewsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent("google_profile_click", { location: "verified_card" })}
                  className="inline-flex items-center gap-1.5 rounded-md border border-border bg-secondary px-4 py-2 text-sm font-bold text-secondary-foreground hover:bg-secondary/80"
                >
                  Google Business Profile
                </a>
              </div>
            </div>

            <div className="p-7 md:p-10 bg-secondary/40">
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
                Linked official profiles
              </p>
              <ul className="mt-4 space-y-2.5">
                <ProfileLink href={biz.googleReviewsUrl} icon={Globe} label="Google Business Profile" sub="Reviews & verified location" track="google_profile_click" />
                <ProfileLink href={biz.instagramUrl} icon={Instagram} label="Instagram @woodseerautos" sub="Workshop photos & daily work" track="instagram_click" />
                <ProfileLink href={biz.linktreeUrl} icon={Link2} label="Linktree" sub="All Woodseer Autos channels" track="linktree_click" />
              </ul>

              <div className="mt-6 rounded-xl border border-border bg-card p-4">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-primary">Owner & operator</p>
                <p className="mt-2 text-sm text-foreground font-semibold">
                  Independently owned and operated by the Woodseer Autos team.
                </p>
                <p className="mt-1.5 text-xs text-muted-foreground leading-relaxed">
                  Not a franchise. Not a referral platform. The workshop you call is the workshop your car goes into.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Fact({ icon: Icon, label, children }: { icon: React.ComponentType<{ className?: string }>; label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-border bg-background/60 p-3">
      <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
        <Icon className="h-3.5 w-3.5 text-primary" />
        {label}
      </div>
      <div className="mt-1.5 text-sm text-foreground font-semibold leading-snug">{children}</div>
    </div>
  );
}

function ProfileLink({ href, icon: Icon, label, sub, track }: { href: string; icon: React.ComponentType<{ className?: string }>; label: string; sub: string; track: string }) {
  if (!href) return null;
  return (
    <li>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackEvent(track as Parameters<typeof trackEvent>[0], { location: "verified_card" })}
        className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5 hover:bg-card transition-colors"
      >
        <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary border border-primary/20">
          <Icon className="h-4 w-4" />
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-bold text-foreground">{label}</span>
          <span className="block text-xs text-muted-foreground">{sub}</span>
        </span>
      </a>
    </li>
  );
}
